import {createReducer} from 'redux-act';
import {getWizard} from 'features/wizardMs/reducers/wizardData/wizardData';
import {WizardOptionType, WizardTreeType, WizardValueType, getUsages} from '@optimaxdev/wizard-ms';
import {AxiosResponse} from 'axios';
import {ApplicationStoreType} from 'reducers';
import {createSelector} from 'reselect';
import {includes, pathOr, pipe, reduceBy, sortBy, toPairs, equals, propOr} from 'ramda';
import {
    BIFOCAL,
    DISTANCE_AND_READING,
    DISTANCE_AND_READING_DESCRIPTION,
    NOPRESCRIPTION,
    OFFICE_NEAR,
    PROGRESSIVES,
} from 'features/wizardMs/constants/usages';
import {getCustomerConfiguration} from 'features/wizardMs/reducers/customerConfiguration/customerConfiguration';

export type UsageItemType = WizardValueType & {parentNodeId: number};

export type GroupedUsageCollectionType = {
    parentNodeId: number;
    title: string;
    sku: string;
    subtitle: string;
    description: string;
    sort: number;
    node: string;
    values: WizardValueType[];
};

export const initialState: WizardOptionType = {
    sort: 1,
    type: 'option',
    alias: 'usage',
    title: 'What do you use your glasses for?',
    required: 1,
    node: 0,
    items: [],
};

/**
 * Usages reducer
 */
export const usages = createReducer<WizardOptionType>({}, initialState)
    /**
     * Subscribe actions
     * https://www.npmjs.com/package/redux-act#typescript
     */
    .on(
        `${getWizard}_FULFILLED`,
        (
            state: WizardOptionType,
            {data}: AxiosResponse<{tree: WizardTreeType}>,
        ): WizardOptionType => {
            const usageNode =
                data.tree.items.find(options => options.alias === 'usage') || initialState;
            return {
                ...usageNode,
                items: getUsages(data.tree),
            };
        },
    );
/**
 * Provides the usage node
 * @param {ApplicationStoreType} store - store
 * @returns {WizardOptionType} node
 */
export const getUsageNode = (store: ApplicationStoreType): WizardOptionType =>
    store.wizardMs.usages;

/**
 * Provides the usage items
 * @param {ApplicationStoreType} store - store
 * @returns {WizardValueType[]} node items
 */
export const getUsagesItems = (store: ApplicationStoreType): WizardValueType[] =>
    store.wizardMs.usages.items || [];

/**
 * Filtered the office lens usage items
 * @param {ApplicationStoreType} store - store
 * @returns {WizardValueType[]} node items
 */
export const getUsagesExcludingOffice = createSelector(getUsagesItems, items => {
    return items.filter(item => item.sku !== OFFICE_NEAR);
});

/**
 * Provides selected usage node
 */
export const getActiveUsage = createSelector(
    getUsageNode,
    getCustomerConfiguration,
    (usageNode, configuration): WizardValueType | {} =>
        (usageNode.items || []).find(
            usageItem => usageItem.node === configuration.options[usageNode.node],
        ) || {},
);

/**
 * Selector for experiment progressive funnel
 */
export const getActiveUsages = createSelector(getActiveUsage, (selectedUsage): {
    isMultifocalSelected: boolean;
    isProgressiveSelected: boolean;
} => ({
    isMultifocalSelected:
        (selectedUsage as UsageItemType)?.sku === PROGRESSIVES ||
        (selectedUsage as UsageItemType)?.sku === BIFOCAL,
    isProgressiveSelected: (selectedUsage as UsageItemType)?.sku === PROGRESSIVES,
}));

/**
 * Get active usage sku
 */
export const getActiveUsageSku = createSelector(
    getActiveUsage,
    (value: WizardValueType | {}): string => ('sku' in value ? value.sku : ''),
);

/**
 * Checks if selected usage is non prescription
 */
export const isFashionUsage = createSelector(
    getActiveUsage,
    pipe(propOr('', 'sku'), equals(NOPRESCRIPTION)),
);

/** ******* functions and selectors only for ui ************ */

/**
 * Accumulates the Usage items PROGRESSIVES, BIFOCAL to a single value.
 * @param {Array<string, WizardValueType>} accumulator - Accumulated value.
 * @param {WizardValueType} value - Usage item
 * @returns {GroupedUsageCollectionType} Accumulated usage collection
 */
export const accumulateUsage = (
    accumulator: GroupedUsageCollectionType | WizardValueType | {values: []},
    value: WizardValueType,
): GroupedUsageCollectionType | WizardValueType =>
    includes(value.sku, [PROGRESSIVES, BIFOCAL])
        ? {
              ...DISTANCE_AND_READING_DESCRIPTION,
              parentNodeId: Number(value.parentNodeId),
              values: (accumulator as GroupedUsageCollectionType).values
                  ? (accumulator as GroupedUsageCollectionType).values.concat(
                        value as UsageItemType,
                    )
                  : [],
              sort: 'sort' in accumulator ? Math.min(accumulator.sort, value.sort) : value.sort,
              usageName: 'Multifocal',
              title: 'Distance & Reading',
          }
        : value;

/**
 * grouped progressive and bifocal lenses
 * @param {WizardValueType[]} items - usage items
 * @returns {GroupedUsageCollectionType[]} grouped items
 */
const groupItems = items => {
    return pipe(
        reduceBy<WizardValueType, WizardValueType | GroupedUsageCollectionType | {values: []}>(
            accumulateUsage,
            {values: []},
        )((
            item, // TODO - GUSA-9265 S.Kokalin seems it should be used as the third argument for reduceBy as keyFn
        ) => (includes(item.sku, [PROGRESSIVES, BIFOCAL]) ? DISTANCE_AND_READING : `${item.node}`)),
        toPairs,
        sortBy(pathOr('sort', [1, 'sort'])),
    )(items);
};

/**
 * Groups the PROGRESSIVES and BIFOCAL to under one DISTANCE_AND_READING property.
 */
export const getGroupedUsages = createSelector(getUsagesItems, items => groupItems(items));

/**
 * Groups filtered usages the PROGRESSIVES and BIFOCAL to under one DISTANCE_AND_READING property.
 */
export const getGroupedUsagesExcludingOffice = createSelector(getUsagesExcludingOffice, items =>
    groupItems(items),
);

/**
 * Is any usage option selected?
 */
export const isUsageSelected = createSelector(getActiveUsageSku, activeUsageSku =>
    Boolean(activeUsageSku),
);
